import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { Sparkles, Copy, Check, FileText, History } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { value: "script", label: "Webinar script", targetField: null, isJson: false },
  { value: "landing_copy", label: "Landing page copy", targetField: "landing_copy", isJson: true },
  { value: "email_template", label: "Email templates", targetField: "email_templates", isJson: true },
  { value: "whatsapp_template", label: "WhatsApp templates", targetField: "whatsapp_templates", isJson: true },
  { value: "slide_outline", label: "Slide deck outline", targetField: null, isJson: false },
];

function safeParseJson(s) {
  try { return { ok: true, val: JSON.parse(s) }; }
  catch (e) { return { ok: false, err: e.message }; }
}

export default function AIStudio() {
  const [kind, setKind] = useState("script");
  const [topic, setTopic] = useState("");
  const [tags, setTags] = useState("");
  const [tone, setTone] = useState("friendly");
  const [webinarId, setWebinarId] = useState("");
  const [webinars, setWebinars] = useState([]);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    api.get("/webinars").then((r) => setWebinars(r.data));
    loadHistory();
  }, []);

  const loadHistory = () => api.get("/ai/history").then((r) => setHistory(r.data));

  const activeKind = KINDS.find((k) => k.value === kind);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/ai/generate", {
        kind, topic, tone,
        webinar_id: webinarId || null,
        problem_tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setOutput(data.output);
      toast.success("Generated");
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const copyOut = () => { navigator.clipboard.writeText(output); toast.success("Copied"); };

  const apply = async () => {
    if (!webinarId) { toast.error("Choose a webinar to apply to"); return; }
    if (!activeKind.targetField) { toast.error("This content type has no target field"); return; }
    setApplying(true);
    try {
      let value = output;
      if (activeKind.isJson) {
        const parsed = safeParseJson(output);
        if (!parsed.ok) throw new Error("Output is not valid JSON: " + parsed.err);
        value = parsed.val;
      }
      const patch = { [activeKind.targetField]: value };
      await api.patch(`/webinars/${webinarId}`, patch);
      toast.success(`Applied to ${activeKind.targetField.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setApplying(false); }
  };

  const loadFromHistory = (h) => {
    setKind(h.kind); setTopic(h.topic || ""); setOutput(h.output || "");
    if (h.webinar_id) setWebinarId(h.webinar_id);
    setHistoryOpen(false);
    toast.success("Loaded from history");
  };

  return (
    <div className="space-y-6 vp-fade-up">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="vp-label">AI Content Studio</div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium leading-tight tracking-[-0.025em] mt-1">
            Draft everything <span className="font-serif-italic text-[#0B5CFF]">in your voice.</span>
          </h1>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">Scripts, landing copy, emails, WhatsApp — generated in seconds, editable in one click. Apply directly to a webinar when ready.</p>
        </div>
        <button onClick={() => setHistoryOpen((v) => !v)} data-testid="ai-history-toggle" className="vp-btn-ghost text-sm inline-flex items-center gap-2">
          <History className="w-4 h-4" strokeWidth={1.5} /> History ({history.length})
        </button>
      </div>

      {historyOpen && (
        <div className="vp-glass p-4">
          <div className="vp-label mb-3">Recent generations</div>
          <ul className="divide-y divide-[#E4E6EC]">
            {history.length === 0 && <li className="text-sm text-[#747680] py-3">No generations yet.</li>}
            {history.map((h) => (
              <li key={h.id} className="flex items-center gap-3 py-3">
                <FileText className="w-4 h-4 text-[#0B5CFF] shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0B0D17] truncate">{h.topic || "(no topic)"}</div>
                  <div className="text-xs text-[#747680]">{h.kind} · {new Date(h.created_at).toLocaleString("en-IN")}</div>
                </div>
                <button onClick={() => loadFromHistory(h)} className="vp-btn-ghost text-xs py-1.5 px-3">Load</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={generate} className="vp-glass p-6 space-y-4">
          <div>
            <div className="vp-label mb-2">What to generate</div>
            <select data-testid={TIDS.aiKindSelect} className="vp-input" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => (<option key={k.value} value={k.value}>{k.label}</option>))}
            </select>
          </div>
          <div>
            <div className="vp-label mb-2">Topic</div>
            <input data-testid={TIDS.aiTopicInput} className="vp-input" required value={topic}
              onChange={(e) => setTopic(e.target.value)} placeholder="Sell your first cohort in 30 days" />
          </div>
          <div>
            <div className="vp-label mb-2">Link to webinar (optional)</div>
            <select className="vp-input" value={webinarId} onChange={(e) => setWebinarId(e.target.value)} data-testid="ai-webinar-select">
              <option value="">— none —</option>
              {webinars.map((w) => (<option key={w.id} value={w.id}>{w.title}</option>))}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="vp-label mb-2">Problem tags</div>
              <input className="vp-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="low-sales, no-audience" />
            </div>
            <div>
              <div className="vp-label mb-2">Tone</div>
              <select className="vp-input" value={tone} onChange={(e) => setTone(e.target.value)}>
                <option value="friendly">Friendly</option>
                <option value="bold">Bold</option>
                <option value="warm">Warm</option>
                <option value="hinglish">Hinglish</option>
              </select>
            </div>
          </div>
          <button data-testid={TIDS.aiGenerateBtn} disabled={loading || !topic} type="submit"
            className="vp-btn-primary inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> {loading ? "Generating…" : "Generate"}
          </button>
        </form>

        <div className="vp-glass p-6 flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="vp-label">Output</div>
            <div className="flex items-center gap-2">
              {output && (
                <button onClick={copyOut} className="vp-btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              )}
              {output && activeKind?.targetField && (
                <button onClick={apply} disabled={applying || !webinarId} data-testid="ai-apply-btn"
                  className="vp-btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> {applying ? "Applying…" : "Apply to webinar"}
                </button>
              )}
            </div>
          </div>
          {output && activeKind?.targetField && !webinarId && (
            <div className="text-xs text-[#0B5CFF] mb-3">Choose a webinar in the left panel to enable "Apply".</div>
          )}
          <textarea data-testid={TIDS.aiOutput}
            className="vp-input flex-1 min-h-[420px] max-h-[560px] font-mono text-xs whitespace-pre-wrap resize-y"
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="Your generated content will appear here. Edit before applying." />
          {activeKind?.isJson && output && (
            <div className="text-xs mt-2 text-[#747680]">
              JSON payload — will be applied to <span className="font-mono text-[#0B5CFF]">{activeKind.targetField}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
